/**
 * Closing orders already resting on the legs about to be exited — 9.34.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A NEW EXIT CANNOT SIMPLY IGNORE THEM
 *
 * The server reserves a resting closing order's quantity (`alreadySent`), so
 * a leg wholly covered by one is EXCLUDED from a new exit. That is the guard
 * that stops the same holding being closed twice, and it is right.
 *
 * Its consequence is what a trader meets: a 50% exit on a four-leg basket
 * with one leg resting fills three and leaves the fourth untouched, and the
 * basket is then half-off with a working order nobody mentioned.
 *
 * So the exit has to deal with the resting order rather than step around
 * it — cancel it, then close the leg with everything else. This file is the
 * part that decides WHICH orders those are, which is the half worth testing
 * without a drawer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONLY CLOSING ORDERS, AND ONLY ON THE LEGS BEING EXITED
 *
 * An ENTRY resting on the same contract is somebody's working buy, and
 * cancelling it because a different position is being closed would be the
 * exit reaching outside its own scope — the same hazard `tokens` had when
 * one cross closed two legs.
 *
 * Matched on TOKEN, because that is what both sides carry: the order book
 * speaks in broker tokens and so does the open book. The account is checked
 * too, since one contract can rest in two of them.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { isWorking } from '@/store/orders'
import type { TrackedIntent } from '@/store/orders'
import type { OpenBookTrade } from './useOpenBook'

/** The shape this needs from an order-book row. Narrow on purpose. */
export interface RestingOrderSource {
  /**
   * ENTRY / EXIT / SQUARE_OFF, when it is known.
   *
   * Absent on a live engine event, which carries no kind — and absent
   * READS AS AN ENTRY here, so an unlabelled working order is never
   * cancelled. Erring that way costs a trader one manual cancel; erring
   * the other way cancels an order nobody asked about.
   */
  readonly kind?: string | null
  readonly broker?: { readonly connectionId: string } | null
  readonly legs: readonly {
    readonly state: TrackedIntent['state']
    readonly token: number
    readonly symbol?: string | null
    readonly orderRowId?: string | null
    readonly limitPrice?: number | null
  }[]
}

/** One order that must go before the exit can be sized honestly. */
export interface RestingExit {
  readonly orderRowId: string
  readonly token: number
  readonly symbol: string | null
  readonly limitPrice: number | null
}

/**
 * The resting closing orders sitting on these trades' held legs.
 *
 * Deduplicated by `orderRowId`: a sliced leg can appear more than once and
 * cancelling the same row twice is a second request for no second effect.
 */
export function restingExitsFor(
  trades: readonly OpenBookTrade[],
  orders: readonly RestingOrderSource[],
  /** Narrower than the trades when one leg's own cross was pressed. */
  onlyLegTokens?: readonly number[],
): RestingExit[] {
  const wanted = new Map<string, Set<number>>()
  for (const t of trades) {
    const held = t.legs.filter((l) => l.netQty !== 0 && l.token !== null)
    if (held.length === 0) continue
    const set = wanted.get(t.broker.connectionId) ?? new Set<number>()
    for (const l of held) {
      if (onlyLegTokens !== undefined && !onlyLegTokens.includes(l.token!)) continue
      set.add(l.token!)
    }
    wanted.set(t.broker.connectionId, set)
  }

  const out = new Map<string, RestingExit>()
  for (const o of orders) {
    // An entry resting on the same strike belongs to somebody else's plan.
    if (o.kind !== 'EXIT' && o.kind !== 'SQUARE_OFF') continue
    const tokens = o.broker == null ? undefined : wanted.get(o.broker.connectionId)
    if (tokens === undefined) continue
    for (const leg of o.legs) {
      if (!isWorking(leg.state)) continue
      if (!tokens.has(leg.token)) continue
      const rowId = leg.orderRowId ?? null
      // No row id is no handle to cancel by — a sliced leg. Reported by the
      // caller as "cancel it yourself" rather than silently skipped.
      if (rowId === null) continue
      out.set(rowId, {
        orderRowId: rowId,
        token: leg.token,
        symbol: leg.symbol ?? null,
        limitPrice: leg.limitPrice ?? null,
      })
    }
  }
  return [...out.values()]
}

/** "2 pending orders" / "1 pending order". */
export const restingCountLabel = (n: number): string =>
  `${n} pending order${n === 1 ? '' : 's'}`
